// @flow

import React, { type Node, useCallback, useMemo } from 'react';
import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import { useUniqueId, useMustShow } from '@tectonics/hooks';
import { type Country  } from '@tectonics/prs/country';
import { getZipcodeNormalizer } from '@tectonics/prs/customer/normalize';
import { useFieldMetadata } from '@tectonics/prs/form';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { serverErrorMessage, type UserInputValidationError } from '@tectonics/prs/validation';
import { Input, Label, Empty } from '../../../atoms';
import { Field } from '../../../molecules';

type Props = {|
  +fieldName: string,
  +placeholder?: string,
  +mask: ArrayMask,
  +labelText?: string,
  +validationErrorMessage?: string,
|};

export function FieldCustomerZipcode({
  fieldName,
  placeholder = '',
  mask,
  labelText,
  validationErrorMessage,
}: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const mustShow = useMustShow(fieldName);
  const inputId = useUniqueId();
  const { submitting, validationError, formHasBeenSent } = useFieldMetadata(fieldName);

  const formatRaw = useCallback((rawValue: string) => formatValueWithMask(rawValue, mask), [mask]);
  const normalizer = useMemo(() => getZipcodeNormalizer(country), [country]);

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(fieldName, formatRaw, normalizer);

  if (!mustShow)
    return <Empty />;

  const labelTextContent = labelText || getDefaultLabelText(country);

  return (
    <Field
      data-test-name="address-zipcode"
      label={<Label htmlFor={inputId}>{labelTextContent}</Label>}
      showFloatingLabel={!!value}
      hasError={validationError && formHasBeenSent}
      error={validationErrorMessage || getDefaultValidationErrorMessage(country, validationError)}
    >
      <Input
        as={InputMask}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        data-secret
        fashion={formHasBeenSent && validationError ? 'error' : null}
        id={inputId}
        mask={mask}
        onChange={handleChange}
        placeholder={labelTextContent}
        readOnly={submitting}
        type="tel"
        value={value}
        name="address-zipcode"
      />
    </Field>
  );
}

function getDefaultLabelText(country: Country): Node {
  switch (country) {
    case 'mx':
    case 'co':
      return 'Código postal';

    default:
      throw new Error(`There is no label text for country: "${country}".`);
  }
}

function getDefaultValidationErrorMessage(country: Country, validationError: ?UserInputValidationError): string {
  if (!validationError) {
    return 'El Código Postal que informaste no es válido.';
  }

  switch (country) {
    case 'mx':
    case 'co':
      return getSpanishValidationErrorMessage(validationError);

    default:
      throw new Error(`There is no error message for country: "${country}".`);
  }
}

function getSpanishValidationErrorMessage(validationError: UserInputValidationError): string {
  switch (validationError.id) {
    case 'empty':
      return '¡Falta poco!, informa tu Código Postal.';
    case 'server-error':
      return serverErrorMessage(validationError.payload);
    default:
      return 'El Código Postal que informaste no es válido.';
  }
}
